import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import type { SitePageEntity } from '@common/types/site-page.type';
import type { Domain } from '@common/types/domain.type';
import type { SubscriptionMessageModalPayload } from './subscription-message-modal/subscription-message-modal.component';
import { SubscriptionMessageModalComponent } from './subscription-message-modal/subscription-message-modal.component';
import type { FormAdditionalSettingsFormGroupType } from './sp-form-additional-settings.type';
import {
    type FormAdditionalSettingsFormType,
    type PromoCodeSettingsFormType,
} from './sp-form-additional-settings.type';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input } from '@angular/core';

import { EActionAfterConfirm, ECaptchaLimits } from '@common/enums';
import { InputBoolean } from '@common/helpers/convert';
import type { DeviceMode } from '@common/types/layout.type';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SpControlHandleComponent } from '@ui/sp-control-handle';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpSwitchComponent } from '@ui/sp-switch';
import { TranslateModule } from '@ngx-translate/core';

import { HtmlToTextPipe } from '@common/pipes/html-to-text.pipe';
import { type PromoCode, PromoCodeDiscountType } from '@common/types/promo-codes.type';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
    selector: 'sp-form-additional-settings',
    templateUrl: './sp-form-additional-settings.component.html',
    styleUrls: ['./sp-form-additional-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        SpControlHandleComponent,
        BsDropdownModule,
        TooltipModule,
        SpSwitchComponent,
        TranslateModule,
        HtmlToTextPipe,
        NgSelectModule,
        FormsModule,
    ],
})
export class SpFormAdditionalSettingsComponent {
    private readonly modalService = inject(BsModalService);
    private readonly ref = inject(ChangeDetectorRef);

    @Input() public isHasSuccessPageOption: boolean;
    @Input() public formAdditionalSettings!: FormAdditionalSettingsFormGroupType;
    @Input() public pages: SitePageEntity[] = [];
    @Input() public siteDomains: Domain[] = [];
    @Input() public baseHref: string;
    @Input() public deviceMode: DeviceMode;
    @Input() addChatBotToForm: boolean = false;
    @Input() @InputBoolean() public isPaymentForm: boolean = false;
    @Input() @InputBoolean() public isEduForm: boolean = false;
    @Input() @InputBoolean() public isShopCart: boolean = false;
    @Input() public promoCodes: PromoCode[] = [];

    private modalRef: BsModalRef = null;

    public readonly EActionAfterConfirm = EActionAfterConfirm;
    public readonly ECaptchaLimits = ECaptchaLimits;
    public readonly promoCodeDiscountType = PromoCodeDiscountType;

    public setOpenLink(page: SitePageEntity): void {
        this.openLink.patchValue(this.getLink(page));
    }

    public showSubscriptionMessageModal() {
        this.modalRef = this.modalService.show(SubscriptionMessageModalComponent, {
            initialState: {
                subscriptionMessage: this.subscriptionMessage.value,
                baseHref: this.baseHref,
                deviceMode: this.deviceMode,
                isShopCart: this.isShopCart,
            },
        });

        this.modalRef.content.saveData.subscribe((data: SubscriptionMessageModalPayload) => {
            this.subscriptionMessage.patchValue(data.cachedText);
            this.ref.markForCheck();
        });
    }

    private getLink(page: SitePageEntity): string {
        return page.url;
        // if (Boolean(this.siteDomains.length)) {
        //     return getFullDomainNameHandlerWithProtocol(this.siteDomains) + '/' + page.url;
        // }
        //
        // return `https://${page.sub_domain}.${page.domain}/${page.url}`;
    }

    public get actionAfterConfirmType(): FormAdditionalSettingsFormType['actionAfterConfirmType'] {
        return this.formAdditionalSettings.get(
            'actionAfterConfirmType'
        ) as FormAdditionalSettingsFormType['actionAfterConfirmType'];
    }

    public get subscriptionMessage(): FormAdditionalSettingsFormType['subscriptionMessage'] {
        return this.formAdditionalSettings.get(
            'subscriptionMessage'
        ) as FormAdditionalSettingsFormType['subscriptionMessage'];
    }

    public get openLink(): FormAdditionalSettingsFormType['openLink'] {
        return this.formAdditionalSettings.get('openLink') as FormAdditionalSettingsFormType['openLink'];
    }

    public get updateExistingContact(): FormAdditionalSettingsFormType['updateExistingContact'] {
        return this.formAdditionalSettings.get(
            'updateExistingContact'
        ) as FormAdditionalSettingsFormType['updateExistingContact'];
    }

    public get isEnabledCaptcha(): FormAdditionalSettingsFormType['isEnabledCaptcha'] {
        return this.formAdditionalSettings.get(
            'isEnabledCaptcha'
        ) as FormAdditionalSettingsFormType['isEnabledCaptcha'];
    }

    public get captchaLimit(): FormAdditionalSettingsFormType['captchaLimit'] {
        return this.formAdditionalSettings.get('captchaLimit') as FormAdditionalSettingsFormType['captchaLimit'];
    }

    public get promoCodeSettings(): FormAdditionalSettingsFormType['promoCodeSettings'] {
        return this.formAdditionalSettings.get(
            'promoCodeSettings'
        ) as FormAdditionalSettingsFormType['promoCodeSettings'];
    }

    public get isEnabledPromoCode(): PromoCodeSettingsFormType['isEnabled'] {
        return this.promoCodeSettings.get('isEnabled') as PromoCodeSettingsFormType['isEnabled'];
    }

    public get selectedPromoCode(): PromoCodeSettingsFormType['selectedPromoCode'] {
        return this.promoCodeSettings.get('selectedPromoCode') as PromoCodeSettingsFormType['selectedPromoCode'];
    }

    public get labelPromoCode(): PromoCodeSettingsFormType['label'] {
        return this.promoCodeSettings.get('label') as PromoCodeSettingsFormType['label'];
    }

    public get placeholderPromoCode(): PromoCodeSettingsFormType['placeholder'] {
        return this.promoCodeSettings.get('placeholder') as PromoCodeSettingsFormType['placeholder'];
    }

    public get isEnableCorporateEmail(): FormAdditionalSettingsFormType['isEnableCorporateEmail'] {
        return this.formAdditionalSettings.get(
            'isEnableCorporateEmail'
        ) as FormAdditionalSettingsFormType['isEnableCorporateEmail'];
    }

    public setUpdateData(value: boolean): void {
        this.updateExistingContact.setValue(value);
    }

    public setEnabledPromoCode(value: boolean): void {
        this.isEnabledPromoCode.setValue(value);
    }

    public setPromoCode(value: number[]): void {
        this.selectedPromoCode.setValue(value);
    }

    public handleIsEnabledCaptcha(value: boolean): void {
        this.isEnabledCaptcha.setValue(value);
    }

    public handleIsEnabledCorporateEmail(value: boolean): void {
        this.isEnableCorporateEmail.setValue(value);
    }

    public get selectedPromoValues(): string {
        if (!this.selectedPromoCode?.value || !this.promoCodes?.length) {
            return '';
        }

        const discounts: string[] = [];

        this.selectedPromoCode.value.forEach((id) => {
            const promoCode = this.promoCodes.find((code) => code.id === Number(id));
            if (promoCode) {
                (promoCode.discounts ?? []).forEach((discount) => {
                    discounts.push(
                        `${discount.value} ${
                            discount.type === this.promoCodeDiscountType.money ? ' ' + discount.currency : '%'
                        }`
                    );
                });
            }
        });

        return discounts.join(', ');
    }

    /**
     * First discount of a promo code, formatted for the picker row. Returns an empty
     * string when the code carries no discounts — the API shape allows it, and a
     * blind `discounts[0].value` in the template would break the whole option list.
     */
    public promoCodeDiscountLabel(promoCode: PromoCode): string {
        const discount = promoCode.discounts?.[0];

        if (!discount) {
            return '';
        }

        return `${discount.value}${discount.type === this.promoCodeDiscountType.money ? ' ' + discount.currency : ' %'}`;
    }
}
